import { Component, effect, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Dataset, ItemListResponse, Permission } from '@models';
import { PermissionService, NotificationService, AuthService } from '@services';
import { Observable } from 'rxjs';
import { PermissionFormComponent } from '../form/form.component';
import { PromptComponent } from '@components/prompt/prompt.component';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { ResolveUserPipe, RoleStringPipe } from '@pipes';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';

@Component({
  selector: 'app-permission-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatListModule,
    MatPaginatorModule,
    ResolveUserPipe,
    RoleStringPipe,
  ],
})
export class PermissionListComponent {
  route = inject(ActivatedRoute);
  notificationService = inject(NotificationService);
  dialog = inject(MatDialog);
  authService = inject(AuthService);
  permissionService = inject(PermissionService);
  router = inject(Router);

  dataset = signal<Dataset>(this.route.snapshot.data['dataset']);
  permissions = signal<Permission[]>([]);
  selectedPermission = signal<Permission | null>(null);
  count = signal<number>(0);

  page = signal<PageEvent>({
    pageIndex: parseInt(this.route.snapshot.queryParams['page'] || 0),
    pageSize: parseInt(this.route.snapshot.queryParams['pageSize'] || 10),
  } as PageEvent);

  constructor() {
    // navigate on filter changes
    effect(() => {
      const page = this.page();
      this.router.navigate(['/dataset', this.dataset().id, 'permission'], {
        queryParams: {
          page: page.pageIndex || undefined,
          pageSize: page.pageSize === 10 ? undefined : page.pageSize,
        },
      });
    });

    this.route.queryParams.subscribe((params: Params) => {
      this.page.update((p) => {
        return {
          ...p,
          pageIndex: params['page'] || 0,
          pageSize: params['pageSize'] || 10,
        };
      });

      this.reloadItems();
    });
  }

  edit() {
    const dialogRef = this.dialog.open(PermissionFormComponent, {
      data: this.selectedPermission(),
      minWidth: '360px',
      width: '480px',
    });

    dialogRef
      .afterClosed()
      .subscribe((updatedPermission: Permission | null) => {
        if (!updatedPermission) {
          return;
        }

        let actionResult: Observable<Permission>;

        if (this.selectedPermission()?.userId) {
          actionResult = this.permissionService.updatePermission(
            this.dataset().id,
            updatedPermission,
          );
        } else {
          actionResult = this.permissionService.createPermission(
            this.dataset().id,
            updatedPermission,
          );
        }

        actionResult.subscribe({
          next: () => {
            this.reloadItems();
          },
          error: (err) => {
            switch (err?.status) {
              case 409:
                this.notificationService.info(
                  'User already has a role. No changes were made.',
                );
                break;
              default:
                console.error(err);
                this.notificationService.error('Failed to update permission.');
            }
          },
        });
      });
  }

  delete() {
    const dialogRef = this.dialog.open(PromptComponent, {
      data: {
        title: 'Delete permissions',
        content: 'Are you sure you want to revoke selected user permissions?',
      },
      minWidth: '360px',
      width: '480px',
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) {
        return;
      }

      this.permissionService
        .deletePermission(this.dataset().id, this.selectedPermission()!.userId)
        .subscribe({
          next: () => {
            this.selectedPermission.set(null);
            this.reloadItems();
          },
          error: (err) => {
            console.error(err);
            this.notificationService.error('Failed to delete permission.');
          },
        });
    });
  }

  reloadItems() {
    this.permissionService
      .getPermissions(
        this.dataset().id,
        this.page().pageIndex * this.page().pageSize,
        this.page().pageSize,
        true,
      )
      .subscribe({
        next: (result: ItemListResponse<Permission>) => {
          this.permissions.set(result.items);
          this.count.set(result.count);
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to load items.');
        },
      });
  }
}
